/**
 * // Class Obj: モーダルの portal 化（DOM退避・復帰）を管理
 * // Note:
 * // - モーダルを body 直下へ移動し、z-index や親要素の overflow の影響を受けにくくします。
 * // - 元の位置にはコメントノードを差し込み、閉じるときに元のDOM位置へ復帰します。
 */
export class ModalPortal
{
  private readonly mPlaceholderMap: WeakMap<HTMLElement, Comment> = new WeakMap();
  private readonly mParentMap: WeakMap<HTMLElement, Node> = new WeakMap();
  /**
   * // Method: 指定したモーダルが body 直下へ退避済みか判定する
   */
  public isPortaled(modal: HTMLElement): boolean
  {
    return this.mPlaceholderMap.has(modal);
  }

  /**
   * // Method: モーダルを body 直下へ移動する
   * // Note:
   * // - 元位置には復帰用のコメントノードを残します。
   * // - 二重 portal を避けるため、退避済みの場合は何もしません。
   */
  public portal(modal: HTMLElement): void
  {
    if (this.isPortaled(modal))
    {
      return;
    }

    const parent = modal.parentNode;
    if (!parent)
    {
      return;
    }

    const placeholder = document.createComment('modal-origin');
    parent.insertBefore(placeholder, modal);

    this.mParentMap.set(modal, parent);
    this.mPlaceholderMap.set(modal, placeholder);

    document.body.appendChild(modal);
  }

  /**
   * // Method: body 直下へ退避したモーダルを元のDOM位置へ戻す
   */
  public restore(modal: HTMLElement): void
  {
    const placeholder = this.mPlaceholderMap.get(modal);
    const parent = this.mParentMap.get(modal);

    if (!placeholder || !parent)
    {
      return;
    }

    placeholder.parentNode?.insertBefore(modal, placeholder);
    placeholder.remove();

    this.mPlaceholderMap.delete(modal);
    this.mParentMap.delete(modal);
  }

  /**
   * // Method: 複数のモーダルをまとめて元のDOM位置へ戻す
   */
  public restoreAll(modals: HTMLElement[]): void
  {
    modals.forEach((modal: HTMLElement) =>
    {
      this.restore(modal);
    });
  }
}
